import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import api from '../services/api';

const ForgotPassword = () => {
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { register, handleSubmit, formState: { errors } } = useForm();
  const orgId = new URLSearchParams(location.search).get('org');

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      
      // Call the forgot password endpoint
      // The endpoint expects email and optionally org_id in the request body
      const requestBody = {
        email: data.email
      };
      
      // Include org_id if present in URL
      if (orgId) {
        requestBody.org_id = orgId;
      }
      
      await api.post('/auth/forgot-password', requestBody);
      
      setEmailSent(true);
      toast.success('Password reset email sent! Please check your inbox.');
    } catch (error) {
      console.error('Forgot password error:', error);
      console.error('Error response data:', error.response?.data);
      
      // Handle different error response formats
      let errorMessage = 'Failed to send password reset email. Please try again.';
      let loginUrl = null;
      
      if (error.response?.data) {
        const errorData = error.response.data;
        console.log('Error data detail:', errorData.detail);
        console.log('Error data detail type:', typeof errorData.detail);
        
        // Handle validation error array (FastAPI format)
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } 
        // Handle object detail with loginUrl (for employer/staff)
        else if (typeof errorData.detail === 'object' && errorData.detail !== null) {
          errorMessage = errorData.detail.message || errorData.detail.detail || errorMessage;
          // Try both camelCase and snake_case
          loginUrl = errorData.detail.loginUrl || errorData.detail.login_url || null;
          console.log('Extracted loginUrl:', loginUrl);
        }
        // Handle string detail
        else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
        // Handle message field
        else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      // Show error with login URL if available
      if (loginUrl) {
        const message = `${errorMessage}\n\nYour organization login URL:\n${loginUrl}\n\nClick the toast to go to your login page.`;
        toast.error(message, {
          duration: 15000,
          style: { 
            maxWidth: '600px', 
            whiteSpace: 'pre-line',
            wordBreak: 'break-all',
            fontSize: '14px',
            lineHeight: '1.6'
          },
          onClick: () => {
            window.location.href = loginUrl;
          }
        });
        // Also log to console for debugging
        console.log('Login URL available:', loginUrl);
      } else {
        console.warn('No login URL found in error response');
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="auth-page">
        <div className="max-w-md w-full space-y-8 auth-card text-center">
          <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Check Your Email
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            We've sent a password reset link to your email address. Please check your inbox and click the link to reset your password.
          </p>
          <p className="mt-4 text-center text-xs text-gray-500">
            The link will expire in 1 hour. If you don't see the email, please check your spam folder.
          </p>
          <div className="mt-6">
            <Link
              to={orgId ? `/login?org=${orgId}` : "/login"}
              className="w-full btn-primary"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="max-w-md w-full space-y-8 auth-card">
        <div>
          <div className="mx-auto h-16 w-16 bg-primary-100 rounded-full flex items-center justify-center">
            <svg className="h-10 w-10 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Forgot Your Password?
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter your email address and we'll send you a link to reset your password.
          </p>
          {!orgId && (
            <p className="mt-1 text-center text-xs text-gray-500">
              Note: This page is for candidates and administrators. Employers and staff members should use their organization-specific login page.
            </p>
          )}
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              className={`appearance-none relative block w-full px-3 py-2 border ${
                errors.email ? 'border-red-300' : 'border-gray-300'
              } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm`}
              placeholder="Enter your email address"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </button>
          </div>

          <div className="text-center">
            <Link 
              to={orgId ? `/login?org=${orgId}` : "/login"} 
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Back to Login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword;

